// src/components/login/SignupForm.js
import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { ref, set } from 'firebase/database';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { auth, database } from '../../firebase';
import emailService from '../../services/emailService';

const SignupForm = ({ onSwitchMode, setError, redirectDestination, onStepChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Basic info, 2: Address info, 3: Account details
  const [redirectAfterSignup, setRedirectAfterSignup] = useState(null);
  const [returnStep, setReturnStep] = useState(null);
  
  // Check for redirect parameter and return step
  useEffect(() => {
    // Check URL parameters
    const searchParams = new URLSearchParams(location.search);
    const redirectParam = searchParams.get('redirect');
    const returnStepParam = searchParams.get('returnStep');
    
    if (redirectParam) {
      setRedirectAfterSignup(redirectParam);
    } else if (redirectDestination) {
      setRedirectAfterSignup(redirectDestination);
    }

    if (returnStepParam) {
      setReturnStep(returnStepParam);
    }
    
    // Also check for pending booking in localStorage
    const pendingBooking = localStorage.getItem('pendingBooking');
    if (pendingBooking) {
      try {
        const bookingData = JSON.parse(pendingBooking);
        if (bookingData.returnPath) {
          // Store the return path rather than just 'booking'
          setRedirectAfterSignup('booking');
        }
        if (bookingData.returnToVehicleStep) {
          setReturnStep('vehicle');
        }
      } catch (e) {
        console.error('Error parsing pending booking:', e);
      }
    }
  }, [location, redirectDestination]);
  
  // Notify parent component of step change if needed
  useEffect(() => {
    if (onStepChange) {
      onStepChange(step);
    }
  }, [step, onStepChange]);
  
  // Save user profile to database with all information
  const saveUserProfile = (user, additionalData) => {
    return set(ref(database, 'users/' + user.uid), {
      email: user.email,
      firstName: additionalData.firstName || '',
      lastName: additionalData.lastName || '',
      phoneNumber: additionalData.phoneNumber || '',
      address: additionalData.address || '',
      city: additionalData.city || '',
      state: additionalData.state || '',
      zipCode: additionalData.zipCode || '',
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString()
    });
  };
  
  // Handle next step
  const handleNextStep = (e) => {
    e.preventDefault();
    
    if (step === 1) {
      if (!firstName || !lastName) {
        setError('Please provide both first and last name');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    }
  };
  
  // Handle previous step
  const handlePrevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };
  
  // Handle signup final step
  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      console.log('Attempting signup with:', email);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log('Signup successful:', userCredential.user.uid);
      
      // Save additional user info
      try {
        await saveUserProfile(userCredential.user, {
          firstName,
          lastName,
          phoneNumber,
          address,
          city,
          state,
          zipCode
        });
        console.log('User profile saved to database');

        // Send welcome email via Microsoft Graph
        try {
          await emailService.sendUserWelcomeEmail({
            email: userCredential.user.email,
            firstName: firstName
          });
          console.log('Welcome email sent successfully');
        } catch (emailError) {
          console.error('Failed to send welcome email:', emailError);
          // Don't fail the signup process if email fails
        }

      } catch (dbError) {
        console.error('Error saving user profile:', dbError);
        // Continue with navigation even if profile saving fails
      }
      
      // Handle redirection based on pending booking
      if (redirectAfterSignup === 'booking') {
        // Get the detailed booking information from localStorage
        const pendingBooking = localStorage.getItem('pendingBooking');
        
        if (pendingBooking) {
          try {
            // Parse the booking data
            const bookingData = JSON.parse(pendingBooking);
            
            // Mark that authentication is completed
            bookingData.userAuthCompleted = true;
            
            // Store updated booking data back to localStorage
            // This is important to ensure the booking flow continues with auth user
            localStorage.setItem('pendingBooking', JSON.stringify(bookingData));
            
            // Navigate to the exact path that was stored
            if (bookingData.returnPath) {
              console.log('Redirecting to:', bookingData.returnPath);
              navigate(bookingData.returnPath);
            } else {
              // Fallback to standard booking route
              navigate('/booking');
            }
          } catch (e) {
            console.error('Error processing pending booking:', e);
            navigate('/booking'); // Fallback
          }
        } else {
          navigate('/booking'); // No pending booking data found
        }
      } else {
        navigate('/account'); // Default redirection if not from booking
      }
    } catch (error) {
      console.error('Error during signup:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      // Show the actual error message if we don't have a specific handler
      if (getErrorMessage(error.code) === 'An error occurred. Please try again') {
        setError(`Firebase error: ${error.message}`);
      } else {
        setError(getErrorMessage(error.code));
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Convert Firebase error codes to user-friendly messages
  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/invalid-email':
        return 'Invalid email address format';
      case 'auth/user-disabled':
        return 'This account has been disabled';
      case 'auth/email-already-in-use':
        return 'This email is already registered';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection';
      case 'auth/operation-not-allowed':
        return 'This signup method is not enabled';
      case 'auth/missing-password':
        return 'Please enter a password';
      case 'auth/internal-error':
        return 'An internal Firebase error occurred. Please try again';
      case 'auth/configuration-not-found':
        return 'Firebase authentication not properly configured. Please contact support.';
      default:
        return 'An error occurred. Please try again';
    }
  };
  
  return (
    <form onSubmit={step === 3 ? handleSignup : handleNextStep}>
      {step === 1 && (
        <>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#3A98B9] focus:border-[#3A98B9] sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#3A98B9] focus:border-[#3A98B9] sm:text-sm"
              />
            </div>
          </div>
          
          <div className="mb-4">
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number (optional)
            </label>
            <input
              id="phoneNumber"
              name="phoneNumber"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#3A98B9] focus:border-[#3A98B9] sm:text-sm"
            />
          </div>
        </>
      )}
      
      {step === 2 && (
        <>
          <div className="mb-4">
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              id="address"
              name="address"
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#3A98B9] focus:border-[#3A98B9] sm:text-sm"
              placeholder="123 Main St, Apt 4B"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                id="city"
                name="city"
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#3A98B9] focus:border-[#3A98B9] sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                id="state"
                name="state"
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#3A98B9] focus:border-[#3A98B9] sm:text-sm"
              />
            </div>
          </div>
          
          <div className="mb-4">
            <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
              ZIP Code
            </label>
            <input
              id="zipCode"
              name="zipCode"
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#3A98B9] focus:border-[#3A98B9] sm:text-sm"
            />
          </div>
        </>
      )}
      
      {step === 3 && (
        <>
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#3A98B9] focus:border-[#3A98B9] sm:text-sm"
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none block w-full pl-10 pr-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#3A98B9] focus:border-[#3A98B9] sm:text-sm"
                placeholder="Create a password"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      
      <div className="flex justify-between mt-6">
        {step > 1 && (
          <button
            type="button"
            onClick={handlePrevStep}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Back
          </button>
        )}
        
        <button
          type="submit"
          disabled={loading}
          className={`flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#3A98B9] hover:bg-[#2A87A8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3A98B9] ${
            loading ? 'opacity-70 cursor-not-allowed' : ''
          } ${step === 1 ? 'ml-auto' : ''}`}
        >
          {loading ? (
            <span>Processing...</span>
          ) : (
            <>
              <span>{step < 3 ? 'Next' : 'Create account'}</span>
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </button>
      </div>
      
      {step === 1 && (
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?
            {' '}
            <button
              type="button"
              onClick={onSwitchMode}
              className="font-medium text-[#3A98B9] hover:text-[#2A87A8]"
            >
              Sign in
            </button>
          </p>
        </div>
      )}
      
      {redirectAfterSignup === 'booking' && step === 1 && (
        <div className="mt-4 text-sm text-gray-600 p-3 bg-gray-50 rounded-lg">
          <p className="text-center">
            Your account will be used for your current booking and future services.
          </p>
        </div>
      )}
    </form>
  );
};

export default SignupForm;
